/**
 * Manila timezone (UTC+8) date utilities.
 * Ensures all interview dates display correctly in Philippine time
 * regardless of the user's browser timezone.
 */

/**
 * Convert an ISO date string (or Date) to a Date object whose local getters
 * (getFullYear, getMonth, getDate, getHours, etc.) return Manila time values.
 * This allows date-fns format() to output Manila time correctly.
 */
export function toManilaDate(isoString) {
  const date = typeof isoString === 'string' ? new Date(isoString) : isoString;
  const utcMs = date.getTime() + date.getTimezoneOffset() * 60000;
  return new Date(utcMs + 8 * 60 * 60000);
}

/**
 * Get current date/time as if in Manila timezone.
 */
export function getManilaNow() {
  return toManilaDate(new Date());
}

/**
 * Get today's date string in Manila timezone (YYYY-MM-DD).
 * Useful for date input min attributes to allow same-day scheduling.
 */
export function getManilaTodayStr() {
  const now = getManilaNow();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Check if a Manila-converted date is today in Manila timezone.
 */
export function isManilaToday(manilaDate) {
  const now = getManilaNow();
  return manilaDate.getFullYear() === now.getFullYear() &&
    manilaDate.getMonth() === now.getMonth() &&
    manilaDate.getDate() === now.getDate();
}

/**
 * Check if a Manila-converted date is tomorrow in Manila timezone.
 */
export function isManilaTomorrow(manilaDate) {
  const tomorrow = getManilaNow();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return manilaDate.getFullYear() === tomorrow.getFullYear() &&
    manilaDate.getMonth() === tomorrow.getMonth() &&
    manilaDate.getDate() === tomorrow.getDate();
}

/**
 * Get the first-of-month date string (YYYY-MM-01) for the current Manila calendar month.
 * Useful for analytics/report range defaults so "this month" anchors on Manila, not the
 * viewer's device timezone.
 */
export function getManilaMonthStartStr() {
  const now = getManilaNow();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}-01`;
}

/**
 * Format a timestamp (ISO string, epoch ms, or Date) in Asia/Manila time, regardless of the
 * viewer's device timezone. Thin wrapper over toLocaleDateString/toLocaleString.
 * Pass `{ withTime: true }` to include time-of-day (uses toLocaleString); otherwise date-only
 * (uses toLocaleDateString). Any other options are forwarded to Intl as-is; `timeZone` is
 * always forced to 'Asia/Manila' so callers can't accidentally drop it.
 */
export function formatManilaDate(value, opts = {}) {
  const { withTime, locale, ...intlOpts } = opts;
  const date = value instanceof Date ? value : new Date(value);
  const formatOpts = { ...intlOpts, timeZone: 'Asia/Manila' };
  return withTime ? date.toLocaleString(locale, formatOpts) : date.toLocaleDateString(locale, formatOpts);
}
